import { cardEditContext } from '@context/card-edit-context';
import { type FormEvent, useCallback, useContext, useEffect, useState } from 'react';

import { DeleteDialog } from '../../DeleteDialog';
import { EditDialog } from '../../EditDialog';
import { ResponsiveDialog } from '../../ResponsiveDialog';
import { UnlinkConfirmationDialog } from '../../UnlinkConfirmationDialog';
import type { ValueProvider } from '.cache/__types__';

type FieldEditorProps = {
  title: string;
  id: string;
  updateTempValueProviderForTour: (valueProvider: ValueProvider | null) => void;
  fieldNode: HTMLDivElement | HTMLButtonElement | undefined;
  fieldBtnNode: HTMLDivElement | HTMLButtonElement | undefined;
  children: React.ReactElement;
  positionDeps?: React.DependencyList;
  hasUnsavedChanges: boolean;
  canDelete: boolean;
  isFieldLinkedToProfile?: boolean;
  validator?: () => boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  unlinkCardField?: (valueProvider?: ValueProvider) => void;
};

export const FieldEditor: React.FC<FieldEditorProps> = ({
  title,
  id,
  updateTempValueProviderForTour,
  fieldNode,
  fieldBtnNode,
  children,
  positionDeps = [],
  hasUnsavedChanges,
  isFieldLinkedToProfile,
  unlinkCardField,
  canDelete,
  validator,
  onCancel,
  onSave,
  onDelete,
}) => {
  const [showDeletePrompt, setShowDeletePrompt] = useState<boolean>(false);
  const [showUnlinkWarningPrompt, setShowUnlinkWarningPrompt] = useState(false);
  const {
    activeAttributeId,
    makeInactive: requestToHideEdit,
    pendingCardField,
    setPendingCardField,
    onEditEvent,
  } = useContext(cardEditContext);

  const showEditDialog = activeAttributeId === id;

  useEffect(() => {
    if (showEditDialog) {
      onEditEvent?.('open', title);
    }
  }, [showEditDialog, onEditEvent, title]);

  const handleClose = useCallback(() => {
    requestToHideEdit(id);
    fieldBtnNode?.focus();
  }, [requestToHideEdit, id, fieldBtnNode]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isFieldLinkedToProfile && hasUnsavedChanges) {
      // Show the unlink dialog if the fields are linked and changes have been made to the field
      return setShowUnlinkWarningPrompt(true);
    }

    onEditEvent?.('save', title);
    onSave();
    handleClose();
  };

  const handleSubmitLinkedField = () => {
    unlinkCardField?.('member');
    handleClose();
    setShowUnlinkWarningPrompt(false);
  };

  const handleDelete = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onDelete) {
      return;
    }

    if (pendingCardField) {
      setPendingCardField(undefined);
      handleClose();
      return;
    }

    onEditEvent?.('delete', title);
    setShowDeletePrompt(false);
    handleClose();
    onDelete();
  };

  return (
    <ResponsiveDialog
      show={showEditDialog}
      updateTempValueProviderForTour={updateTempValueProviderForTour}
      handleClose={() => {
        onEditEvent?.('cancel', title);
        onCancel();
        handleClose();
      }}
      targetNode={fieldNode}
      positionDeps={positionDeps}
    >
      {canDelete && showDeletePrompt ? (
        <DeleteDialog
          title={title}
          onCancel={() => setShowDeletePrompt(false)}
          onClose={handleClose}
          onSubmit={handleDelete}
        />
      ) : isFieldLinkedToProfile && showUnlinkWarningPrompt ? (
        <UnlinkConfirmationDialog
          title={title}
          onCancel={() => setShowUnlinkWarningPrompt(false)}
          onSave={handleSubmitLinkedField}
        />
      ) : (
        <EditDialog
          title={title}
          canDelete={canDelete}
          canSubmit={validator?.() ?? false}
          onCancel={() => {
            onEditEvent?.('cancel', title);
            onCancel();
            handleClose();
          }}
          onSubmit={handleSubmit}
          onDelete={() => setShowDeletePrompt(true)}
        >
          {children}
        </EditDialog>
      )}
    </ResponsiveDialog>
  );
};
